import {
  diffDays,
  toDate,
  toLocalDateISOString,
  toLocalDateTimeISOString,
  toLocalTime,
} from "@/utils/Dates";
import {
  CompanyRoleIdWithName,
  getSharesForShareholder,
  ProjectContext,
  ProjectJsonData,
  SharesForShareholder,
} from "./commonDomain";
import { CompanyRole } from "@/types/CompanyInfo";
import { RoleType } from "@/types/enums/RoleType";
import { getFullName } from "@/utils/Roles";
import { identity } from "lodash";
import { ShareholderDTO } from "@/types/Shareholder";
import { Language } from "@/i18n/languages";
import { GeneralMeetingVotingModel } from "@/dbot/generalMeetingMinutes/generalMeetingMinutesDomain";
import { TMessage } from "@/i18n/tt";
import { DocumentType } from "@/common/documents";
import { MeetingForm, MeetingPlace } from "@/meetings/meetingsDomain";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  RadioFieldModel,
  SelectableWithIdModel,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import { LocalizedText } from "@/common/common";
import { ShareType } from "@/common/share";
import { AlertProps } from "@/components-v2/Alert";
import { DecisionType } from "@/common/decisions";
import { when } from "@/utils/functions";
import { FeatureToggles } from "@/common/featureToggles";

export type GeneralMeeting = {
  meetingStartDate?: string;
  isTimeSet?: boolean;
  meetingForm: GeneralMeetingForm;
  meetingPlace: MeetingPlace;
  isSimplifiedGeneralMeeting: boolean;
  inlineAddressValue?: string;
  conductElectronicGeneralMeeting: boolean;
  includeListOfParticipantsInMinutes?: boolean;
  meetingChair?: CompanyRoleIdWithName;
  coSigner?: CompanyRoleIdWithName;
  additionalCoSigners: CompanyRoleIdWithName[];
  meetingPlaceholderVisible?: boolean;
  generalMeetingType?: GeneralMeetingType;
};

export type GeneralMeetingFields = {
  dateGeneralMeeting: LocalDateFieldModel;
  timeGeneralMeeting: TimeFieldModel;
  isTimeSet: BooleanFieldModel;
  meetingForm: RadioFieldModel<GeneralMeetingForm>;
  generalMeetingType: RadioFieldModel<GeneralMeetingType>;
  meetingPlace: RadioFieldModel<MeetingPlace>;
  isSimplifiedGeneralMeeting: BooleanFieldModel;
  inlineAddressValue: StringFieldModel;
  conductElectronicGeneralMeeting: BooleanFieldModel;
  includeListOfParticipantsInMinutes: BooleanFieldModel;
  meetingChair: RadioFieldModel<CompanyRoleIdWithName | undefined>;
  coSigner: RadioFieldModel<CompanyRoleIdWithName | undefined>;
  additionalCoSigners: SelectableWithIdModel;
  meetingPlaceholderVisible: BooleanFieldModel;
  displayConfirmElectronicGMDialog: BooleanFieldModel;
  hasLiveGeneralMeetingFeature: boolean;
};

export function createGeneralMeetingFields({
  roles,
  features,
  jsonData,
  onChange,
  hasMultipleShareholders = false,
  isBatchProject = false,
}: {
  roles: CompanyRole[];
  features: FeatureToggles;
  jsonData: ProjectJsonData;
  onChange: () => void;
  hasMultipleShareholders?: boolean;
  isBatchProject?: boolean;
}): GeneralMeetingFields {
  const generalMeeting = jsonData.generalMeeting;
  const companyRoles = roles.map((r) => mapRoleToCompanyRoleIdWithName(r));
  const displayConfirmElectronicGMDialog = new BooleanFieldModel({ value: false });
  const isTimeSet = new BooleanFieldModel({ value: generalMeeting?.isTimeSet ?? false });
  const timeGeneralMeeting = new TimeFieldModel({
    value: isCirculationWithoutTime(generalMeeting)
      ? undefined
      : when(generalMeeting?.meetingStartDate, toLocalTime),
    onChange: (time) => {
      isTimeSet.setValue(time !== undefined);
      onChange();
    },
  });

  const coSigner = new RadioFieldModel({
    value: generalMeeting?.coSigner,
    options: companyRoles,
    idFn: (option) => option?.companyroleId.toString() ?? "",
    labelFn: (option) => ({ translated: option?.name ?? "" }),
    onChange,
    required: generalMeeting?.isSimplifiedGeneralMeeting === false,
  });
  const simplifiedGeneralMeetingModel = new BooleanFieldModel({
    value: generalMeeting?.isSimplifiedGeneralMeeting,
    onChange: (value) => {
      coSigner.setRequired(!value);
      onChange();
    },
  });
  return {
    dateGeneralMeeting: new LocalDateFieldModel({
      value: when(generalMeeting?.meetingStartDate, toLocalDateISOString),
      onChange,
    }),
    timeGeneralMeeting,
    isTimeSet,
    meetingForm: createMeetingFormField(
      generalMeeting?.meetingForm ?? GeneralMeetingForm.physical_meeting,
      (meetingForm) => {
        if (meetingForm === GeneralMeetingForm.circulation && !isTimeSet.value) {
          timeGeneralMeeting.setAndCommitInputValue(undefined);
        } else if (meetingForm !== GeneralMeetingForm.circulation && timeGeneralMeeting.isEmpty) {
          timeGeneralMeeting.setAndCommitInputValue(defaultMeetingTime);
          isTimeSet.setValue(false);
        }
        onChange();
      },
    ),
    meetingPlace: createMeetingPlaceField(
      generalMeeting?.meetingPlace ?? MeetingPlace.companysOffice,
      onChange,
    ),
    isSimplifiedGeneralMeeting: simplifiedGeneralMeetingModel,
    inlineAddressValue: new StringFieldModel({
      value: generalMeeting?.inlineAddressValue,
      onChange,
    }),
    conductElectronicGeneralMeeting: new BooleanFieldModel({
      value: generalMeeting?.conductElectronicGeneralMeeting,
      onChange: (flag) => {
        if (flag && !features.liveGM) {
          displayConfirmElectronicGMDialog.setValue(true);
        }
        onChange();
      },
    }),
    includeListOfParticipantsInMinutes: new BooleanFieldModel({
      value: generalMeeting?.includeListOfParticipantsInMinutes,
      requiredTrue: hasMultipleShareholders || isBatchProject,
      onChange,
    }),
    meetingChair: new RadioFieldModel({
      value: generalMeeting?.meetingChair,
      options: companyRoles,
      idFn: (option) => option?.companyroleId.toString() ?? "",
      labelFn: (option) => ({ translated: option?.name ?? "" }),
      onChange,
    }),
    coSigner: coSigner,
    additionalCoSigners: new SelectableWithIdModel({
      value:
        generalMeeting?.additionalCoSigners.map((cosigner) => ({
          id: cosigner.companyroleId.toString(),
          name: cosigner.name,
          isSelected: true,
        })) ?? [],
      onChange,
    }),
    meetingPlaceholderVisible: new BooleanFieldModel({ onChange }),
    displayConfirmElectronicGMDialog,
    generalMeetingType: createGeneralMeetingTypeField(
      generalMeeting?.generalMeetingType ?? GeneralMeetingType.regular,
      onChange,
    ),
    hasLiveGeneralMeetingFeature: features.liveGM ?? false,
  };
}

export function mapRoleToCompanyRoleIdWithName(role: CompanyRole) {
  return {
    companyroleId: role.companyroleId,
    name: getFullName(role),
  };
}

export function isCirculationInvalid(
  option: GeneralMeetingForm,
  simplifiedGeneralMeetingModel: BooleanFieldModel,
): boolean {
  return option === GeneralMeetingForm.circulation && simplifiedGeneralMeetingModel.value !== true;
}

export function prepareGeneralMeetingForSave(
  generalMeetingFields: GeneralMeetingFields,
  jsonData: ProjectJsonData,
): GeneralMeeting | undefined {
  if (!hasGeneralMeeting(jsonData)) {
    return undefined;
  }

  return {
    meetingStartDate: prepareGeneralMeetingDate(generalMeetingFields),
    isTimeSet: generalMeetingFields.isTimeSet.value ?? false,
    meetingForm: generalMeetingFields.meetingForm.value,
    meetingPlace: generalMeetingFields.meetingPlace.value,
    isSimplifiedGeneralMeeting: generalMeetingFields.isSimplifiedGeneralMeeting.value ?? true,
    inlineAddressValue: generalMeetingFields.inlineAddressValue.isEmpty
      ? undefined
      : generalMeetingFields.inlineAddressValue.validatedValue,
    conductElectronicGeneralMeeting:
      generalMeetingFields.conductElectronicGeneralMeeting.value ?? false,
    includeListOfParticipantsInMinutes:
      generalMeetingFields.includeListOfParticipantsInMinutes.value ?? false,
    meetingChair: generalMeetingFields.meetingChair.value,
    coSigner: generalMeetingFields.coSigner.value,
    additionalCoSigners: generalMeetingFields.additionalCoSigners.value
      .filter((o) => o.isSelected)
      .map((value) => ({
        companyroleId: Number(value.id),
        name: value.name,
      })),
    meetingPlaceholderVisible: generalMeetingFields.meetingPlaceholderVisible.value,
    generalMeetingType: generalMeetingFields.generalMeetingType.value,
  };
}

const defaultMeetingTime = "12:00";

function isCirculationWithoutTime(generalMeeting: GeneralMeeting | undefined) {
  return (
    generalMeeting?.meetingForm === GeneralMeetingForm.circulation && !generalMeeting.isTimeSet
  );
}

function prepareGeneralMeetingDate(generalMeetingFields: GeneralMeetingFields) {
  if (generalMeetingFields.dateGeneralMeeting.validatedValue) {
    const time = generalMeetingFields.timeGeneralMeeting.validatedValue ?? defaultMeetingTime;
    const date = toDate(`${generalMeetingFields.dateGeneralMeeting.validatedValue}T${time}`);
    return toLocalDateTimeISOString(date);
  }
  return undefined;
}

function hasGeneralMeeting(jsonData: ProjectJsonData) {
  return jsonData.documents.some(
    (d) =>
      d.documentData.type === DocumentType.notice_general_meeting ||
      d.documentData.type === DocumentType.general_meeting,
  );
}

export enum GeneralMeetingForm {
  physical_meeting = "physical_meeting",
  electronicGeneralMeeting = "electronicGeneralMeeting",
  circulation = "circulation",
}
export enum GeneralMeetingType {
  regular = "regular",
  extraordinary = "extraordinary",
}

export enum GeneralMeetingElectronicParticipation {
  LINK_GIVEN = "LINK_GIVEN",
  LINK_GIVEN_LATER = "LINK_GIVEN_LATER",
  ELECTRONIC_GM_NOT_OFFERED = "ELECTRONIC_GM_NOT_OFFERED",
}

export type PowerOfAttorneyLanguage = "no" | "en" | "both";

export type GeneralMeetingVotes = {
  meetChair: GeneralMeetingVoting;
  approveNoticeAndAgenda: GeneralMeetingVoting;
  decisions: VotingForDecision[];
};

export type ShareholderParticipation = {
  shareTypeName: string;
  sharesParticipating: number;
  totalShares: number;
  percentage: number;
  votesParticipating: number;
  shareTypeId?: number;
};
export type VotingForDecision = {
  decisionId: string;
  voting: GeneralMeetingVoting;
  votingType?: GeneralMeetingVotingType;
};
export type GeneralMeetingVoting = Live | IndividualVoting | Unanimous | Voted | NotVoted;
export type Live = {
  type: "live";
  votesFor: number;
  votesAgainst: number;
  representedVotes: number;
  shareCapitalFor: number;
  shareCapitalAgainst: number;
  representedShareCapital: number;
};
export type Unanimous = { type: "unanimous" };
export type Voted = {
  type: "voted";
  votesFor: number;
  votesAgainst: number;
  votesBlank: number;
  shareCapitalFor: number;
  shareCapitalAgainst: number;
  shareCapitalBlank: number;
};
export type IndividualVoting = {
  type: "individualVotes";
  votesFor: number;
  votesAgainst: number;
  votesBlank: number;
  shareCapitalFor: number;
  shareCapitalAgainst: number;
  shareCapitalBlank: number;
};
export type NotVoted = { type: "notVoted" };

export const unanimous = (): Unanimous => ({ type: "unanimous" });
export const voted = (
  votesFor = 0,
  votesAgainst = 0,
  votesBlank = 0,
  shareCapitalFor = 0,
  shareCapitalAgainst = 0,
  shareCapitalBlank = 0,
): Voted => ({
  type: "voted",
  votesFor,
  votesAgainst,
  votesBlank,
  shareCapitalFor,
  shareCapitalAgainst,
  shareCapitalBlank,
});
export const individualVotes = (
  votesFor = 0,
  votesAgainst = 0,
  votesBlank = 0,
  shareCapitalFor = 0,
  shareCapitalAgainst = 0,
  shareCapitalBlank = 0,
): IndividualVoting => ({
  type: "individualVotes",
  votesFor,
  votesAgainst,
  votesBlank,
  shareCapitalFor,
  shareCapitalAgainst,
  shareCapitalBlank,
});
export const liveVotes = (
  votesFor = 0,
  votesAgainst = 0,
  representedVotes = 0,
  shareCapitalFor = 0,
  shareCapitalAgainst = 0,
  representedShareCapital = 0,
): Live => ({
  type: "live",
  votesFor,
  votesAgainst,
  representedVotes,
  shareCapitalFor,
  shareCapitalAgainst,
  representedShareCapital,
});
export const notVoted = (): NotVoted => ({ type: "notVoted" });
export const isUnanimous = (v: GeneralMeetingVoting): v is Unanimous => v.type === "unanimous";
export const isVoted = (v: GeneralMeetingVotingModel): boolean => v.type === "voted";

export const defaultVotes = (): GeneralMeetingVotes => ({
  approveNoticeAndAgenda: unanimous(),
  meetChair: unanimous(),
  decisions: [],
});

export type IndividualVote = "for" | "against" | "blank";

export function calculateVotes(
  votes: GeneralMeetingIndividualVote[],
  specifiedVote: IndividualVote,
  shareholderById: { [p: string]: ShareholderDTO },
  sharesForShareholder: SharesForShareholder[],
  shareTypes: ShareType[],
) {
  const specifiedVotes = votes.filter(
    (v) => v.vote === specifiedVote && shareholderById[v.shareholderId] !== undefined,
  );
  if (specifiedVotes.length === 0) return 0;
  return specifiedVotes
    .flatMap((v) =>
      getSharesForShareholder(shareholderById[v.shareholderId], sharesForShareholder).map((d) => {
        const shareType = shareTypes.find((t) => t.sharetypeId === d.shareTypeId);
        return shareType!.vote * d.numberOfShares;
      }),
    )
    .reduce((a, b) => a + b, 0);
}

export function calculateShareCapital(
  votes: GeneralMeetingIndividualVote[],
  specifiedVote: IndividualVote,
  shareholderById: { [p: string]: ShareholderDTO },
  sharesForShareholder: SharesForShareholder[],
  nominalValue: number,
) {
  const specifiedVotes = votes.filter(
    (v) => v.vote === specifiedVote && shareholderById[v.shareholderId] !== undefined,
  );
  if (specifiedVotes.length === 0) return 0;
  return specifiedVotes
    .flatMap((v) =>
      getSharesForShareholder(shareholderById[v.shareholderId], sharesForShareholder).map((d) => {
        return d.numberOfShares * nominalValue;
      }),
    )
    .reduce((a, b) => a + b, 0);
}

export const calculateAllVotes = (
  votes: GeneralMeetingIndividualVote[],
  shareholderById: { [p: string]: ShareholderDTO },
  sharesForShareholder: SharesForShareholder[],
  shareTypes: ShareType[],
  nominalValue: number,
  type: "live" | "individualVotes" = "individualVotes",
  representedVotes?: number,
  representedShareCapital?: number,
): GeneralMeetingVoting => {
  return {
    type,
    votesFor: calculateVotes(votes, "for", shareholderById, sharesForShareholder, shareTypes),
    votesAgainst: calculateVotes(
      votes,
      "against",
      shareholderById,
      sharesForShareholder,
      shareTypes,
    ),
    votesBlank: calculateVotes(votes, "blank", shareholderById, sharesForShareholder, shareTypes),
    shareCapitalFor: calculateShareCapital(
      votes,
      "for",
      shareholderById,
      sharesForShareholder,
      nominalValue,
    ),
    shareCapitalAgainst: calculateShareCapital(
      votes,
      "against",
      shareholderById,
      sharesForShareholder,
      nominalValue,
    ),
    shareCapitalBlank: calculateShareCapital(
      votes,
      "blank",
      shareholderById,
      sharesForShareholder,
      nominalValue,
    ),
    representedVotes,
    representedShareCapital,
  } as GeneralMeetingVoting;
};

export type GeneralMeetingIndividualVote = {
  id: string;
  shareholderId: number;
  vote: IndividualVote;
};

export type NoticeToGeneralMeeting = {
  type: DocumentType.notice_general_meeting;
  publishArticlesOnCompanysWeb?: string;
  companyPublishedDocumentsRegardingResolutions: boolean;
  companyPublishedDocumentsRegardingResolutionsAddress?: string;
  dateNoticeGeneralMeeting?: string;
  noticeGeneralMeetingSender?: string;
  chairperson?: CompanyRoleIdWithName;
  electronicParticipation: GeneralMeetingElectronicParticipation;
  electronicParticipationLink: string;
  inlineAddressValue?: string;
  meetingFormInlineAddress?: string;
  articlesPublishedOnTheAddress?: string;
  repairTextDeadlineGeneralMeeting: boolean;
  documentLanguage: Language[];
  conductElectronicGeneralMeeting?: boolean;
  additionalDetailsTexts?: LocalizedText[];
};

export function noticeToGeneralMeeting(
  data: Partial<NoticeToGeneralMeeting> = {},
): NoticeToGeneralMeeting {
  return {
    type: DocumentType.notice_general_meeting,
    companyPublishedDocumentsRegardingResolutions: false,
    electronicParticipation: GeneralMeetingElectronicParticipation.LINK_GIVEN_LATER,
    electronicParticipationLink: "",
    repairTextDeadlineGeneralMeeting: false,
    documentLanguage: [Language.NO],
    additionalDetailsTexts: data?.additionalDetailsTexts ?? [],
    ...data,
  };
}

export type GeneralMeetingMinutes = {
  type: DocumentType.general_meeting;
  generalMeetingType: GeneralMeetingType;
  companyHasMultipleStockHolders?: boolean;
  meetingChair?: CompanyRoleIdWithName;
  coSigner?: CompanyRoleIdWithName; // Co-signer of the GM protocol
  votes?: GeneralMeetingVotes;
  companySoleStockholder?: string;
  shareholderParticipation: ShareholderParticipation[];
  documentLanguage: Language[];
  additionalDetailsTexts?: LocalizedText[];
};

export function defaultGeneralMeetingDates() {
  return {
    notice: toLocalDateISOString(Date.now()),
    meeting: toLocalDateISOString(Date.now()),
  };
}

export function isTooShortNotice(
  noticeDate: string | undefined,
  meetingDate: string | undefined,
): boolean {
  return Boolean(noticeDate && meetingDate && diffDays(noticeDate, meetingDate) < 7);
}

export function getChairperson(roles: CompanyRole[] = []): CompanyRoleIdWithName | undefined {
  const p = roles.find((r) => r.role === RoleType.CHAIRPERSON);
  if (p) {
    return { companyroleId: p.companyroleId, name: getFullName(p) };
  }
}

export function getCEO(roles: CompanyRole[] = []): CompanyRoleIdWithName | undefined {
  const p = roles.find((r) => r.role === RoleType.CEO);
  if (p) {
    return { companyroleId: p.companyroleId, name: getFullName(p) };
  }
}

export function createMeetingFormField(
  meetingForm: GeneralMeetingForm,
  onChange?: (meetingForm: GeneralMeetingForm) => void,
): RadioFieldModel<GeneralMeetingForm> {
  return new RadioFieldModel<GeneralMeetingForm>({
    value: meetingForm,
    options: Object.values(GeneralMeetingForm),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case GeneralMeetingForm.physical_meeting:
          return { key: "documents.meetingForm.physical_meeting" };
        case GeneralMeetingForm.electronicGeneralMeeting:
          return { key: "documents.meetingForm.electronic" };
        case GeneralMeetingForm.circulation:
          return { key: "documents.meetingForm.circulation" };
      }
    },
    onChange,
  });
}

export function createGeneralMeetingTypeField(
  generalMeetingType: GeneralMeetingType,
  onChange?: () => void,
): RadioFieldModel<GeneralMeetingType> {
  return new RadioFieldModel<GeneralMeetingType>({
    value: generalMeetingType,
    options: Object.values(GeneralMeetingType),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case GeneralMeetingType.extraordinary:
          return { key: "meetings.extraordinary" };
        case GeneralMeetingType.regular:
          return { key: "meetings.ordinary" };
      }
    },
    onChange,
  });
}

export function createMeetingPlaceField(
  meetingPlace: MeetingPlace,
  onChange?: () => void,
): RadioFieldModel<MeetingPlace> {
  return new RadioFieldModel<MeetingPlace>({
    value: meetingPlace,
    options: Object.values(MeetingPlace),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case MeetingPlace.companysOffice:
          return { key: "documents.meetingForm.companys_office" };
        case MeetingPlace.inlineAddress:
          return { key: "documents.meetingForm.inline_address" };
      }
    },
    onChange: onChange,
  });
}

const participationLabels: { [key in GeneralMeetingElectronicParticipation]: TMessage } = {
  [GeneralMeetingElectronicParticipation.LINK_GIVEN]: {
    key: "documents.meetingForm.electronicParticipationLinkGiven",
  },
  [GeneralMeetingElectronicParticipation.LINK_GIVEN_LATER]: {
    key: "documents.meetingForm.electronicParticipationLinkGivenLater",
  },
  [GeneralMeetingElectronicParticipation.ELECTRONIC_GM_NOT_OFFERED]: {
    key: "documents.meetingForm.electronicParticipationNotOffered",
  },
};

export function createElectronicParticipationField(
  electronicParticipation: GeneralMeetingElectronicParticipation,
): RadioFieldModel<GeneralMeetingElectronicParticipation> {
  return new RadioFieldModel<GeneralMeetingElectronicParticipation>({
    value: electronicParticipation,
    options: Object.values(GeneralMeetingElectronicParticipation),
    idFn: identity,
    labelFn: (v) => participationLabels[v],
  });
}

export type GeneralMeetingIndividualVotes = {
  meetChair: GeneralMeetingIndividualVote[];
  approveNoticeAndAgenda: GeneralMeetingIndividualVote[];
  decisions: {
    [key: string]: GeneralMeetingIndividualVote[];
  };
};

export const mapGeneralMeetingFormToMeetingForm = (f: GeneralMeetingForm): MeetingForm => {
  switch (f) {
    case GeneralMeetingForm.physical_meeting:
      return MeetingForm.PHYSICAL;
    case GeneralMeetingForm.electronicGeneralMeeting:
      return MeetingForm.ELECTRONIC;
    case GeneralMeetingForm.circulation:
      return MeetingForm.CIRCULATION;
  }
};

export enum VotingModeOption {
  LIVE_VOTING = "LIVE_VOTING",
  UNANIMOUS = "UNANIMOUS",
  MANUAL = "MANUAL",
  INDIVIDUAL = "INDIVIDUAL",
}

export const votingOptionsLabels: { [key in VotingModeOption]: TMessage } = {
  [VotingModeOption.LIVE_VOTING]: { key: "general-meeting.voting.live" },
  [VotingModeOption.UNANIMOUS]: { key: "general-meeting.voting.unanimous" },
  [VotingModeOption.MANUAL]: { key: "general-meeting.voting.manual" },
  [VotingModeOption.INDIVIDUAL]: { key: "general-meeting.voting.individual" },
};

export const votingOptionsMappings: {
  [key in GeneralMeetingVoting["type"]]: VotingModeOption | undefined;
} = {
  voted: VotingModeOption.MANUAL,
  unanimous: VotingModeOption.UNANIMOUS,
  live: VotingModeOption.LIVE_VOTING,
  individualVotes: VotingModeOption.INDIVIDUAL,
  notVoted: undefined,
};

export enum GeneralMeetingVotingType {
  SIMPLE_MAJORITY = "SIMPLE_MAJORITY", // More than half of registered votes for and against
  QUALIFIED = "QUALIFIED", // More than 2/3 of registered votes for and against, and 2/3 of present capital
  UNANIMOUS = "UNANIMOUS", // All votes for
}

export const generalMeetingVotingTypeSelectedLabels: {
  [key in GeneralMeetingVotingType]: TMessage;
} = {
  [GeneralMeetingVotingType.SIMPLE_MAJORITY]: {
    key: "general-meeting.voting-type.simple.selected",
  },
  [GeneralMeetingVotingType.QUALIFIED]: { key: "general-meeting.voting-type.qualified.selected" },
  [GeneralMeetingVotingType.UNANIMOUS]: { key: "general-meeting.voting-type.unanimous.selected" },
};

export const generalMeetingVotingApprovedLabels = (
  label: TMessage,
  isApproved?: boolean,
): TMessage => {
  if (isApproved) {
    return { key: "general-meeting.voting-type.approved.true", params: { votingTypeLabel: label } };
  } else {
    return {
      key: "general-meeting.voting-type.approved.false",
      params: { votingTypeLabel: label },
    };
  }
};

export const generalMeetingVotingTypeLabels: {
  [key in GeneralMeetingVotingType]: TMessage;
} = {
  [GeneralMeetingVotingType.SIMPLE_MAJORITY]: {
    key: "general-meeting.voting-type.simple.label",
  },
  [GeneralMeetingVotingType.QUALIFIED]: { key: "general-meeting.voting-type.qualified.label" },
  [GeneralMeetingVotingType.UNANIMOUS]: { key: "general-meeting.voting-type.unanimous.label" },
};

export const generalMeetingVotingTypeDescriptions: {
  [key in GeneralMeetingVotingType]: TMessage;
} = {
  [GeneralMeetingVotingType.SIMPLE_MAJORITY]: {
    key: "general-meeting.voting-type.simple.description",
  },
  [GeneralMeetingVotingType.QUALIFIED]: {
    key: "general-meeting.voting-type.qualified.description",
  },
  [GeneralMeetingVotingType.UNANIMOUS]: {
    key: "general-meeting.voting-type.unanimous.description",
  },
};

export const isExtraordinaryGeneralMeeting = (context: ProjectContext): boolean =>
  context.generalMeetingFields.generalMeetingType.value === GeneralMeetingType.extraordinary;

export const annualAccountWarning = (context: ProjectContext): AlertProps | undefined => {
  const isExtraordinaryMeetingWithApproval =
    context.currentDecisions.some((f) => f.type === DecisionType.approveAnnualAccount) &&
    isExtraordinaryGeneralMeeting(context);
  return isExtraordinaryMeetingWithApproval
    ? {
        type: "warning",
        icon: "warning",
        message: { key: "documents.meeting-type.annualAccountsWarning" },
      }
    : undefined;
};
